import { useMemo, useState } from 'react'
import { Button, Group, Loader, Modal, Select } from '@common/ui'
import { Anchor } from '@common/ui/mantine'
import { com } from '@eidu/web'
import { useQuery } from '@tanstack/react-query'
import useEntityById from '../../../../io/useEntityById'
import useRegistryAsset from '../../../../io/useRegistryAsset'
import { getKnownKindKey } from '../../../../domain/kindRegistry'
import listVersions from '../../../../io/api/entity/data/listVersions'
import { RuntimeRef } from '../../../../io/runtimeRef'
import EntityPickerWithRegistry from './EntityPickerWithRegistry'
import EntityTypeId = com.eidu.entity.type.EntityTypeId
import EntityType = com.eidu.entity.type.EntityType
import EntityId = com.eidu.entity.EntityId
import EntityTypeKind = com.eidu.entity.type.EntityTypeKind
import entityIdFromString = com.eidu.entity.entityIdFromString

type RuntimeSelectorProps = {
  entityTypes: ReadonlyMap<EntityTypeId, EntityType>
  value: RuntimeRef | undefined
  onChange: (ref: RuntimeRef | undefined) => void
}

const isRuntimeKind = (kind: EntityTypeKind) => getKnownKindKey(kind) === 'Runtime'

const formatDate = (ms: number): string => new Date(ms).toLocaleString()

const RuntimeSelector = ({ entityTypes, value, onChange }: RuntimeSelectorProps) => {
  const hasRuntimeTypes = useMemo(
    () => [...entityTypes.values()].some((type) => isRuntimeKind(type.kind)),
    [entityTypes]
  )
  const [dialogOpen, setDialogOpen] = useState(false)
  const [pendingRef, setPendingRef] = useState<RuntimeRef | undefined>(value)
  const pendingEntityId = pendingRef ? entityIdFromString(pendingRef.entityId) : undefined
  const { entity: selectedEntity, loading: entityLoading } = useEntityById(
    value ? entityIdFromString(value.entityId) : undefined
  )
  // Fall back to the cross-org registry when the runtime isn't visible in this organization.
  const showRegistryFallback = !!value && !entityLoading && selectedEntity === null
  const { detail: registryDetail, isLoading: registryLoading } = useRegistryAsset(
    showRegistryFallback ? value!.entityId : undefined
  )

  const { entity: pendingLocalEntity, loading: pendingLocalLoading } = useEntityById(pendingEntityId)
  const pendingInRegistry = !!pendingEntityId && !pendingLocalLoading && pendingLocalEntity === null
  const { detail: pendingRegistryDetail, isLoading: pendingRegistryLoading } = useRegistryAsset(
    pendingInRegistry ? pendingRef!.entityId : undefined
  )
  const resolvingPendingSource =
    !!pendingEntityId && (pendingLocalLoading || (pendingInRegistry && pendingRegistryLoading))

  const localVersionsQuery = useQuery({
    queryKey: ['versions', pendingRef?.entityId],
    queryFn: () => listVersions({ id: pendingEntityId! }),
    enabled: !!pendingEntityId && !!pendingLocalEntity,
  })
  const versions = pendingInRegistry ? pendingRegistryDetail?.versions ?? [] : localVersionsQuery.data ?? []

  const runtimeLabel = selectedEntity?.primaryLabelText ?? registryDetail?.name
  const runtimeTypeName = selectedEntity
    ? entityTypes.get(selectedEntity.entity.typeId)?.name
    : registryDetail
      ? 'cross-org'
      : undefined
  const versionSuffix = value?.versionId ? ` @ ${value.versionId.slice(0, 8)}…` : ''
  const isLoading = entityLoading || (showRegistryFallback && registryLoading)

  const versionPin = pendingEntityId && versions.length > 0 && (
    <Select
      label="Pin to version"
      value={pendingRef?.versionId ?? ''}
      onChange={(next) => setPendingRef(pendingRef ? { ...pendingRef, versionId: next || undefined } : pendingRef)}
      allowDeselect={false}
      data={[
        { value: '', label: 'Latest (unpinned)' },
        ...versions.map((record) => ({
          value: record.versionId,
          label: `${record.versionId.slice(0, 8)}… · ${formatDate(record.createdAt)}`,
        })),
      ]}
    />
  )

  return (
    <>
      <Anchor
        component="button"
        size="sm"
        underline="always"
        c="platform"
        onClick={() => {
          setPendingRef(value)
          setDialogOpen(true)
        }}
      >
        {isLoading ? (
          <Loader size="xs" />
        ) : (
          `Runtime: ${
            runtimeLabel ? `${runtimeLabel}${runtimeTypeName ? ` (${runtimeTypeName})` : ''}${versionSuffix}` : 'None'
          }`
        )}
      </Anchor>
      <Modal
        opened={dialogOpen}
        onClose={() => setDialogOpen(false)}
        title="Select Runtime"
        size="lg"
        styles={{ body: { overflow: 'visible' } }}
      >
        <EntityPickerWithRegistry
          value={pendingEntityId}
          onChange={(id: EntityId | undefined) =>
            setPendingRef((prev) => {
              if (!id) return undefined
              const idString = id.toString()
              return { entityId: idString, versionId: prev?.entityId === idString ? prev.versionId : undefined }
            })
          }
          kindFilter={isRuntimeKind}
          registryKind={EntityTypeKind.Companion.Runtime.toString()}
          selectedVersionId={pendingRef?.versionId}
          onRegistryVersionSelect={(versionId) => setPendingRef((prev) => (prev ? { ...prev, versionId } : prev))}
          localPickerExtras={versionPin}
          hasLocalTypes={hasRuntimeTypes}
          valueSource={pendingInRegistry ? 'registry' : pendingLocalEntity ? 'org' : undefined}
          resolvingValueSource={resolvingPendingSource}
          typeLabel="Runtime type"
          entityLabel="Runtime"
          noOptionsText="No runtimes"
        />
        <Group mt="md">
          <Button
            variant="subtle"
            color="negative"
            mr="auto"
            onClick={() => {
              onChange(undefined)
              setDialogOpen(false)
            }}
          >
            Clear
          </Button>
          <Button variant="default" onClick={() => setDialogOpen(false)}>
            Cancel
          </Button>
          <Button
            disabled={!pendingRef}
            onClick={() => {
              onChange(pendingRef)
              setDialogOpen(false)
            }}
          >
            Confirm
          </Button>
        </Group>
      </Modal>
    </>
  )
}

export default RuntimeSelector
